import { useEffect } from '@adornis/functional-lit/stateful.js';
import type { LandingLocale } from '../../shared/content/landingContent.js';
import type { LandingEditorProject } from '../../shared/editor/landingEditorTypes.js';
import { syncInlineTextTypography } from '../editor/landingEditorInlineTypography.js';
import { editorNodeIdClass } from '../editor/landingEditorRender.js';
import type { InlineTextDraft } from '../editor/inlineTextCommit.js';
import {
  numericCssValue,
  scaledCssValue,
  type EditorFrame,
  type EditorLeftTab,
  type InlineTextGeometry,
} from './editorPageConfig.js';
import {
  activeEditorPreviewPage,
  allEditorPreviewPages,
  editorViewportRect,
  queryEditorPreviewNode,
} from './editorPreviewViewport.js';
import type { EditorGeometryOverlayRect } from './editorGeometryState.js';
import { commitActiveVivaProseMirrorInlineEditor } from '../editor/vivaProseMirrorInlineEditor.js';

interface EditorPreviewEffectsOptions {
  isAuthenticated: boolean;
  locale: LandingLocale;
  projectUpdatedAt: string;
  project: LandingEditorProject;
  selectedElementId: string;
  geometryTargetElementId?: string;
  geometryTargetScale?: number;
  leftTab: EditorLeftTab;
  expandedSections: Readonly<Record<string, boolean>>;
  activeFrame: EditorFrame;
  selectedFieldKind?: string;
  inlineTextDraft: InlineTextDraft | null;
  inlineTextGeometry: InlineTextGeometry | null;
  zoom: number;
  isSelectedText: boolean;
  isSelectedIcon: boolean;
  selectedRootSectionId?: string;
  selectedTestimonialId?: string;
  hasSelectedSection: boolean;
  setInlineTextDraft: (value: InlineTextDraft | null) => void;
  setInlineTextGeometry: (value: InlineTextGeometry | null) => void;
  setFloatingToolbarPosition: (value: { left: number; top: number } | null) => void;
  setSectionToolbarPosition: (value: { left: number; top: number } | null) => void;
  setTestimonialToolbarPosition: (value: { left: number; top: number } | null) => void;
  setGeometryOverlayRect: (value: EditorGeometryOverlayRect | null) => void;
  commitInlineTextEditor: (draftOverride?: InlineTextDraft) => void;
  enableSelectedAutoLayout?: () => void;
}

export const useEditorPreviewEffects = ({
  isAuthenticated,
  locale,
  projectUpdatedAt,
  project,
  selectedElementId,
  geometryTargetElementId,
  geometryTargetScale,
  leftTab,
  expandedSections,
  activeFrame,
  selectedFieldKind,
  inlineTextDraft,
  inlineTextGeometry,
  zoom,
  isSelectedText,
  isSelectedIcon,
  selectedRootSectionId,
  selectedTestimonialId,
  hasSelectedSection,
  setInlineTextDraft,
  setInlineTextGeometry,
  setFloatingToolbarPosition,
  setSectionToolbarPosition,
  setTestimonialToolbarPosition,
  setGeometryOverlayRect,
  commitInlineTextEditor,
  enableSelectedAutoLayout,
}: EditorPreviewEffectsOptions) => {
  useEffect(() => {
    if (!enableSelectedAutoLayout || typeof window === 'undefined') return;
    const handleShortcut = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (event.key.toLowerCase() !== 'a' || !event.shiftKey || target?.matches('input, textarea, select, [contenteditable]')) return;
      event.preventDefault();
      enableSelectedAutoLayout();
    };
    window.addEventListener('keydown', handleShortcut);
    return () => window.removeEventListener('keydown', handleShortcut);
  }, [enableSelectedAutoLayout]);

  useEffect(() => {
    if (typeof document === 'undefined' || !inlineTextDraft) return;

    const commitBeforeSelectionChange = (event: PointerEvent) => {
      const target = event.target as HTMLElement | null;
      if (target?.closest('.vp-editor-inline-textarea, .vp-editor-inline-selection-toolbar, .vp-editor-floating-toolbar, .vp-editor-testimonial-toolbar'))
        return;
      const editor = document.querySelector<HTMLElement>('.vp-editor-inline-textarea');
      if (!editor) return;
      editor.dataset.inlineCommitHandled = 'true';
      if (commitActiveVivaProseMirrorInlineEditor()) return;
      commitInlineTextEditor({
        elementId: inlineTextDraft.elementId,
        value: editor.dataset.value ?? inlineTextDraft.value,
      });
    };

    document.addEventListener('pointerdown', commitBeforeSelectionChange, true);
    return () => {
      document.removeEventListener('pointerdown', commitBeforeSelectionChange, true);
    };
  }, [inlineTextDraft?.elementId, commitInlineTextEditor]);

  useEffect(() => {
    if (!isAuthenticated || typeof window === 'undefined') return;
    const frameId = window.requestAnimationFrame(() => {
      allEditorPreviewPages().forEach(page => {
        page.lang = locale;
        syncInlineTextTypography(project, locale, page);
        page.querySelectorAll<HTMLElement>('[contenteditable]').forEach(node => {
          node.removeAttribute('contenteditable');
          node.removeAttribute('spellcheck');
        });
      });
    });
    return () => window.cancelAnimationFrame(frameId);
  }, [isAuthenticated, locale, projectUpdatedAt, selectedElementId, activeFrame]);

  useEffect(() => {
    if (!isAuthenticated || selectedFieldKind !== 'text') {
      setInlineTextDraft(null);
      setInlineTextGeometry(null);
    }
  }, [isAuthenticated, selectedFieldKind]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!isAuthenticated || !inlineTextDraft || selectedFieldKind !== 'text') {
      setInlineTextGeometry(null);
      return;
    }

    let active = true;
    const updateGeometry = () => {
      const cls = editorNodeIdClass(inlineTextDraft.elementId);
      const node = queryEditorPreviewNode(`.vp-editor-preview-page .${cls}`);
      if (!active || !node) {
        if (active) setInlineTextGeometry(null);
        return;
      }

      const rect = editorViewportRect(node);
      const computed = node.ownerDocument.defaultView?.getComputedStyle(node) ?? window.getComputedStyle(node);
      const scale = node.offsetWidth ? rect.width / node.offsetWidth : zoom || 1;
      const lineHeight =
        computed.lineHeight === 'normal'
          ? `${(numericCssValue(computed.fontSize) ?? 16) * 1.48 * scale}px`
          : scaledCssValue(computed.lineHeight, scale);
      setInlineTextGeometry({
        elementId: inlineTextDraft.elementId,
        left: Math.round(rect.left),
        top: Math.round(rect.top),
        width: Math.max(24, Math.round(rect.width)),
        minHeight: Math.max(24, Math.round(rect.height)),
        fontFamily: computed.fontFamily,
        fontSize: scaledCssValue(computed.fontSize, scale),
        fontWeight: computed.fontWeight,
        lineHeight,
        letterSpacing: scaledCssValue(computed.letterSpacing, scale),
        color: computed.color,
        textAlign: computed.textAlign,
      });
    };

    const frameId = window.requestAnimationFrame(updateGeometry);
    const canvasScroll = document.querySelector<HTMLElement>('.vp-editor-canvas-scroll');
    const previewScroll = activeEditorPreviewPage();
    canvasScroll?.addEventListener('scroll', updateGeometry, { passive: true });
    previewScroll?.addEventListener('scroll', updateGeometry, { passive: true });
    window.addEventListener('resize', updateGeometry);
    return () => {
      active = false;
      window.cancelAnimationFrame(frameId);
      canvasScroll?.removeEventListener('scroll', updateGeometry);
      previewScroll?.removeEventListener('scroll', updateGeometry);
      window.removeEventListener('resize', updateGeometry);
    };
  }, [
    isAuthenticated,
    inlineTextDraft?.elementId,
    selectedElementId,
    selectedFieldKind,
    activeFrame,
    zoom,
    locale,
    projectUpdatedAt,
  ]);

  useEffect(() => {
    if (typeof window === 'undefined' || !inlineTextDraft || !inlineTextGeometry) return;
    const frameId = window.requestAnimationFrame(() => {
      const editor = document.querySelector<HTMLElement>('.vp-editor-inline-prosemirror .ProseMirror');
      editor?.focus({ preventScroll: true });
    });
    return () => window.cancelAnimationFrame(frameId);
  }, [inlineTextDraft?.elementId, inlineTextGeometry?.elementId]);

  useEffect(() => {
    if (typeof window === 'undefined' || leftTab !== 'layers') return;
    const frameId = window.requestAnimationFrame(() => {
      document.querySelector<HTMLElement>('.vp-editor-sidebar .vp-layer-entry.is-active')?.scrollIntoView({
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        block: 'nearest',
      });
    });
    return () => window.cancelAnimationFrame(frameId);
  }, [selectedElementId, leftTab, expandedSections]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const frameId = window.requestAnimationFrame(() => {
      const cls = editorNodeIdClass(selectedElementId);
      queryEditorPreviewNode(`.vp-editor-preview-page .${cls}`)?.scrollIntoView({
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        block: 'nearest',
      });
    });
    return () => window.cancelAnimationFrame(frameId);
  }, [selectedElementId]);

  useToolbarPositionEffect({
    enabled: isAuthenticated && (isSelectedText || isSelectedIcon),
    elementId: selectedElementId,
    activeFrame,
    zoom,
    locale,
    projectUpdatedAt,
    setPosition: setFloatingToolbarPosition,
    position: rect => ({
      left: Math.round(rect.left + rect.width / 2),
      top: Math.round(Math.max(68, rect.top - 12)),
    }),
  });

  useToolbarPositionEffect({
    enabled: isAuthenticated && Boolean(selectedRootSectionId) && hasSelectedSection,
    elementId: selectedRootSectionId ?? '',
    activeFrame,
    zoom,
    locale,
    projectUpdatedAt,
    setPosition: setSectionToolbarPosition,
    position: rect => ({
      left: Math.round(Math.max(12, Math.min(window.innerWidth - 12, rect.left + 16))),
      top: Math.round(Math.max(68, rect.top + 12)),
    }),
  });

  useToolbarPositionEffect({
    enabled: isAuthenticated && Boolean(selectedTestimonialId),
    elementId: selectedTestimonialId ?? '',
    activeFrame,
    zoom,
    locale,
    projectUpdatedAt,
    setPosition: setTestimonialToolbarPosition,
    position: rect => ({
      left: Math.round(Math.max(200, Math.min(window.innerWidth - 12, rect.right - 12))),
      top: Math.round(Math.max(68, rect.top + 12)),
    }),
  });

  useGeometryOverlayEffect({
    enabled: isAuthenticated,
    elementId: selectedElementId,
    targetElementId: geometryTargetElementId,
    targetScale: geometryTargetScale,
    activeFrame,
    zoom,
    locale,
    projectUpdatedAt,
    setRect: setGeometryOverlayRect,
  });
};

const useGeometryOverlayEffect = (options: {
  enabled: boolean;
  elementId: string;
  targetElementId?: string;
  targetScale?: number;
  activeFrame: EditorFrame;
  zoom: number;
  locale: LandingLocale;
  projectUpdatedAt: string;
  setRect: (value: EditorGeometryOverlayRect | null) => void;
}) => {
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!options.enabled || !options.elementId) {
      options.setRect(null);
      return;
    }
    let active = true;
    let observer: ResizeObserver | null = null;
    let observedNode: HTMLElement | null = null;
    const updateRect = () => {
      const node = queryEditorPreviewNode(`.vp-editor-preview-page .${editorNodeIdClass(options.targetElementId ?? options.elementId)}`);
      if (!active || !node) {
        if (active) options.setRect(null);
        return;
      }
      const transformedRect = editorViewportRect(node);
      const targetScale = Math.max(0.01, options.targetScale ?? 1);
      const width = transformedRect.width / targetScale;
      const height = transformedRect.height / targetScale;
      const rect = {
        left: transformedRect.left + (transformedRect.width - width) / 2,
        top: transformedRect.top + (transformedRect.height - height) / 2,
        width,
        height,
      };
      options.setRect({
        elementId: options.elementId,
        left: Math.round(rect.left),
        top: Math.round(rect.top),
        width: Math.max(1, Math.round(rect.width)),
        height: Math.max(1, Math.round(rect.height)),
      });
      if (observer && observedNode !== node) {
        if (observedNode) observer.unobserve(observedNode);
        observer.observe(node);
        observedNode = node;
      }
    };
    observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(updateRect);
    const frameId = window.requestAnimationFrame(updateRect);
    const canvasScroll = document.querySelector<HTMLElement>('.vp-editor-canvas-scroll');
    const previewScroll = activeEditorPreviewPage();
    canvasScroll?.addEventListener('scroll', updateRect, { passive: true });
    previewScroll?.addEventListener('scroll', updateRect, { passive: true });
    window.addEventListener('resize', updateRect);
    return () => {
      active = false;
      window.cancelAnimationFrame(frameId);
      canvasScroll?.removeEventListener('scroll', updateRect);
      previewScroll?.removeEventListener('scroll', updateRect);
      window.removeEventListener('resize', updateRect);
      if (observedNode) observer?.unobserve(observedNode);
      observer?.disconnect();
    };
  }, [
    options.enabled,
    options.elementId,
    options.targetElementId,
    options.targetScale,
    options.activeFrame,
    options.zoom,
    options.locale,
    options.projectUpdatedAt,
  ]);
};

interface ToolbarPositionEffectOptions {
  enabled: boolean;
  elementId: string;
  activeFrame: EditorFrame;
  zoom: number;
  locale: LandingLocale;
  projectUpdatedAt: string;
  setPosition: (value: { left: number; top: number } | null) => void;
  position: (rect: DOMRect) => { left: number; top: number };
}

const useToolbarPositionEffect = ({
  enabled,
  elementId,
  activeFrame,
  zoom,
  locale,
  projectUpdatedAt,
  setPosition,
  position,
}: ToolbarPositionEffectOptions) => {
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!enabled || !elementId) {
      setPosition(null);
      return;
    }
    let active = true;
    let observedNode: HTMLElement | null = null;
    let resizeObserver: ResizeObserver | null = null;
    const updatePosition = () => {
      const cls = editorNodeIdClass(elementId);
      const node = queryEditorPreviewNode(`.vp-editor-preview-page .${cls}`);
      if (!active || !node) {
        if (active) setPosition(null);
        return;
      }
      if (resizeObserver && observedNode !== node) {
        if (observedNode) resizeObserver.unobserve(observedNode);
        resizeObserver.observe(node);
        observedNode = node;
      }
      setPosition(position(editorViewportRect(node)));
    };
    resizeObserver =
      typeof ResizeObserver !== 'undefined' ? new ResizeObserver(updatePosition) : null;
    const frameId = window.requestAnimationFrame(updatePosition);
    const canvasScroll = document.querySelector<HTMLElement>('.vp-editor-canvas-scroll');
    const previewScroll = activeEditorPreviewPage();
    canvasScroll?.addEventListener('scroll', updatePosition, { passive: true });
    previewScroll?.addEventListener('scroll', updatePosition, { passive: true });
    window.addEventListener('resize', updatePosition);
    return () => {
      active = false;
      window.cancelAnimationFrame(frameId);
      canvasScroll?.removeEventListener('scroll', updatePosition);
      previewScroll?.removeEventListener('scroll', updatePosition);
      window.removeEventListener('resize', updatePosition);
      if (observedNode) resizeObserver?.unobserve(observedNode);
      resizeObserver?.disconnect();
    };
  }, [enabled, elementId, activeFrame, zoom, locale, projectUpdatedAt]);
};
